import { useTranslation } from "react-i18next";

import DeleteRounded from "@mui/icons-material/DeleteRounded";
import UndoIcon from "@mui/icons-material/Undo";
import { FormHelperText, Stack } from "@mui/material";

import { FileListItem } from "../../../../../../file-list-item/FileListItem";
import { FileUploadArea } from "../../../../../../file-upload-area/FileUploadArea";
import { AssetDocumentZone, ClientDocument, PendingDocumentMove } from "../../../../../api/ClientFileApi";
import {
  useOptionalAssetInventoryDocumentsApi,
  useOptionalDocumentDndApi,
} from "../../../../../api/ClientFileApiContext";
import { ClientFileQueryKey } from "../../../../../query/clientFileQueryKeys";
import { useAssetInventoryDocuments } from "../../../../hooks/useAssetInventoryDocuments";

const SECTION = "clients.client.debtSettlement.assetInventory.dependants";

interface Props {
  clientId: string;
  pendingFiles: File[];
  documentsToDelete: string[];
  documentMoves: PendingDocumentMove[];
  requireDocuments: boolean;
  onDropFiles: (files: File[]) => void;
  onRemovePending: (index: number) => void;
  onToggleDelete: (documentId: string) => void;
  onAddMove: (move: PendingDocumentMove) => void;
  onCancelMove: (documentId: string) => void;
}

export function MaintenanceDocuments({
  clientId,
  pendingFiles,
  documentsToDelete,
  documentMoves,
  requireDocuments,
  onDropFiles,
  onRemovePending,
  onToggleDelete,
  onAddMove,
  onCancelMove,
}: Props) {
  const { t } = useTranslation();
  const documentsApi = useOptionalAssetInventoryDocumentsApi();
  const dndApi = useOptionalDocumentDndApi();
  const { documents, isFetched } = useAssetInventoryDocuments(clientId, {
    queryKey: ClientFileQueryKey.AssetInventoryDependantDocuments,
    getDocuments: (api, params) => api.getAssetInventoryDependantDocuments(params),
  });

  // Optional slice: an app without the asset-inventory document endpoints renders the relatives
  // form without the maintenance-documents block.
  if (!dndApi && !documentsApi) {
    return null;
  }

  const keptDocuments = documents.filter((document) => !documentsToDelete.includes(document.documentId));
  const hasNoDocuments = requireDocuments && isFetched && keptDocuments.length === 0 && pendingFiles.length === 0;

  return (
    <Stack gap={1}>
      {/* The internal app injects the full upload-area section (drop target, rows with the app's file
          actions, pending moves) through the documentDnd slice; the portal gets a plain upload area. */}
      {dndApi ? (
        <dndApi.AssetDocumentSection
          clientId={clientId}
          zone={AssetDocumentZone.Dependants}
          pendingFiles={pendingFiles}
          documentMoves={documentMoves}
          onDropFiles={onDropFiles}
          onRemovePending={onRemovePending}
          onAddMove={onAddMove}
          onCancelMove={onCancelMove}
          gap={1}
        />
      ) : (
        <MaintenanceDocumentsFallback
          documents={documents}
          pendingFiles={pendingFiles}
          documentsToDelete={documentsToDelete}
          onDropFiles={onDropFiles}
          onRemovePending={onRemovePending}
          onToggleDelete={onToggleDelete}
          title={t(`${SECTION}.documentsTitle`)}
        />
      )}
      {hasNoDocuments && (
        <FormHelperText error role="status" sx={{ m: 0 }}>
          {t(`${SECTION}.documentsMissing`)}
        </FormHelperText>
      )}
    </Stack>
  );
}

interface FallbackProps {
  documents: ClientDocument[];
  pendingFiles: File[];
  documentsToDelete: string[];
  onDropFiles: (files: File[]) => void;
  onRemovePending: (index: number) => void;
  onToggleDelete: (documentId: string) => void;
  title: string;
}

// Portal fallback without drag-and-drop: a plain upload area with the saved documents listed below
// and deletes deferred to save (toggle per row).
function MaintenanceDocumentsFallback({
  documents,
  pendingFiles,
  documentsToDelete,
  onDropFiles,
  onRemovePending,
  onToggleDelete,
  title,
}: FallbackProps) {
  const { t } = useTranslation();

  return (
    <FileUploadArea
      title={title}
      onDropFiles={onDropFiles}
      pendingFiles={pendingFiles}
      onRemovePending={onRemovePending}
      uploadButtonPosition="bottom"
      gap={1}
    >
      {documents.map((document) => {
        const markedForDeletion = documentsToDelete.includes(document.documentId);

        return (
          <FileListItem
            key={document.documentId}
            fileName={document.fileName}
            contentType={document.contentType}
            sizeBytes={document.fileSizeBytes}
            struckThrough={markedForDeletion}
            menuItems={[
              {
                key: "toggle-delete",
                label: markedForDeletion ? t("common.fileActions.restore") : t("common.fileActions.delete"),
                icon: markedForDeletion ? <UndoIcon /> : <DeleteRounded />,
                onClick: () => onToggleDelete(document.documentId),
              },
            ]}
          />
        );
      })}
    </FileUploadArea>
  );
}
